import api from './api';

const productService = {
  getProducts: async (params = {}) => {
    const queryParams = new URLSearchParams();

    // Spring Data uses 'page' (0-indexed) and 'size' instead of 'limit'
    if (params.page !== undefined) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('size', params.limit);
    if (params.size) queryParams.append('size', params.size);
    if (params.search) queryParams.append('search', params.search);
    if (params.name) queryParams.append('name', params.name);
    if (params.sku) queryParams.append('sku', params.sku);
    if (params.status) queryParams.append('status', params.status);
    if (params.category) queryParams.append('category', params.category);

    // Convert sort format from 'field:direction' to 'field,direction' for Spring Data
    if (params.sort) {
      const sortParam = params.sort.replace(':', ',');
      queryParams.append('sort', sortParam);
    }

    const queryString = queryParams.toString();
    const url = `/api/admin/products${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);

    // Debug: Log if any products are missing images
    if (response.data?.content && process.env.NODE_ENV === 'development') {
      const productsWithoutImages = response.data.content.filter(p => !p.images || p.images.length === 0);
      if (productsWithoutImages.length > 0) {
        console.log(`[ProductService] ${productsWithoutImages.length} products without images:`,
          productsWithoutImages.map(p => ({ id: p.id, name: p.name }))
        );
      }
      const productsWithImages = response.data.content.filter(p => p.images && p.images.length > 0);
      console.log(`[ProductService] Fetched ${response.data.content.length} products, ${productsWithImages.length} have images`);
    }

    return response;
  },

  getProduct: async (id) => {
    const response = await api.get(`/api/admin/products/${id}`);

    // Debug: Log image status for single product fetch
    if (process.env.NODE_ENV === 'development') {
      const hasImages = response.data?.images && response.data.images.length > 0;
      console.log(`[ProductService] Fetched product ${id}:`, {
        name: response.data?.name,
        hasImages,
        imageCount: response.data?.images?.length || 0
      });
      if (hasImages) {
        console.log(`[ProductService] Product images:`, response.data.images.map(img => ({
          id: img.id,
          isPrimary: img.isPrimary,
          sortOrder: img.sortOrder,
          url: img.url?.substring(0, 50) + '...'
        })));
      }
    }

    return response;
  },

  createProduct: async (data) => {
    const response = await api.post('/api/admin/products', data);

    // Debug: Log image handling in create
    if (process.env.NODE_ENV === 'development') {
      const sentImages = data.images?.length || 0;
      const returnedImages = response.data?.images?.length || 0;
      console.log(`[ProductService] Created product:`, {
        id: response.data?.id,
        name: response.data?.name,
        sentImages,
        returnedImages,
        imagesMatch: sentImages === returnedImages
      });
      if (returnedImages > 0) {
        console.log(`[ProductService] Returned images have sortOrder:`,
          response.data.images.map(img => img.sortOrder)
        );
      }
    }

    return response;
  },

  updateProduct: async (id, data) => {
    const response = await api.put(`/api/admin/products/${id}`, data);

    // Debug: Log image handling in update
    if (process.env.NODE_ENV === 'development') {
      const sentImages = data.images?.length || 0;
      const returnedImages = response.data?.images?.length || 0;
      console.log(`[ProductService] Updated product ${id}:`, {
        name: response.data?.name,
        sentImages,
        returnedImages,
        imagesMatch: sentImages === returnedImages
      });
      if (returnedImages > 0) {
        console.log(`[ProductService] Returned images have sortOrder:`,
          response.data.images.map(img => img.sortOrder)
        );
      }
    }

    return response;
  },

  deleteProduct: async (id) => {
    return api.delete(`/api/admin/products/${id}`);
  },

  getCategories: async () => {
    return api.get('/api/admin/products/categories');
  },

  getLowStockProducts: async () => {
    return api.get('/api/admin/products/low-stock');
  },

  // Generic bulk action (backwards compatible)
  bulkAction: async (action, productIds) => {
    const response = await api.post('/api/admin/products/bulk-action', { action, productIds });
    return response.data;
  },

  // Specific bulk actions
  bulkUpdateStatus: async (productIds, status) => {
    const response = await api.post('/api/admin/products/bulk-action', {
      action: 'update-status',
      productIds,
      status,
    });
    return response.data;
  },

  bulkUpdateField: async (productIds, field, value) => {
    const response = await api.post('/api/admin/products/bulk-action', {
      action: 'update-field',
      productIds,
      field,
      value,
    });
    return response.data;
  },

  bulkAssignCategory: async (productIds, categoryId) => {
    const response = await api.post('/api/admin/products/bulk-action', {
      action: 'assign-category',
      productIds,
      categoryId,
    });
    return response.data;
  },

  bulkDelete: async (productIds) => {
    const response = await api.post('/api/admin/products/bulk-action', {
      action: 'delete',
      productIds,
    });
    return response.data;
  },

  // Featured Products
  getFeaturedProducts: async () => {
    return api.get('/api/admin/products/featured');
  },

  updateFeaturedProducts: async (productIds) => {
    return api.put('/api/admin/products/featured', { productIds });
  },

  addToFeatured: async (productId) => {
    return api.post(`/api/admin/products/featured/${productId}`);
  },

  removeFromFeatured: async (productId) => {
    return api.delete(`/api/admin/products/featured/${productId}`);
  },

  reorderFeatured: async (productIds) => {
    return api.put('/api/admin/products/featured/reorder', { productIds });
  },

  // Validation endpoints
  checkSkuUniqueness: async (sku, productId = null) => {
    const params = new URLSearchParams({ sku });
    if (productId) params.append('productId', productId);
    return api.get(`/api/admin/products/validate/sku?${params.toString()}`);
  },
};

export default productService;
